import { useCallback, useEffect, useRef, useState } from 'react';

import { batchApi } from '../api';
import { useToast } from '../context/ToastContext';

/** The job list, polled while anything is still running. */
export function useJobList({ pollMs = 4000 } = {}) {
  const toast = useToast();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const list = await batchApi.list();
      setJobs(list);
      return list;
    } catch (cause) {
      toast.error(cause, { title: 'Could not load jobs' });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasActive = jobs.some((job) => job.status === 'running' || job.status === 'queued');

  useEffect(() => {
    if (!hasActive) return undefined;
    const timer = setInterval(refresh, pollMs);
    return () => clearInterval(timer);
  }, [hasActive, pollMs, refresh]);

  const remove = useCallback(
    async (id) => {
      try {
        await batchApi.remove(id);
        setJobs((current) => current.filter((job) => job.id !== id));
        return true;
      } catch (cause) {
        // A running job is refused with 409 and the message says to cancel it
        // first, which is worth showing rather than swallowing.
        toast.error(cause, { title: 'Could not delete that job' });
        return false;
      }
    },
    [toast],
  );

  const setPinned = useCallback(
    async (id, pinned) => {
      try {
        await batchApi.setPinned(id, pinned);
        // Refresh rather than patch: pinning changes the order, and the server owns
        // that ordering. Re-sorting here would be a second copy of the same rule.
        await refresh();
        return true;
      } catch (cause) {
        toast.error(cause, {
          title: pinned ? 'Could not pin that job' : 'Could not unpin that job',
        });
        return false;
      }
    },
    [refresh, toast],
  );

  return { jobs, loading, refresh, hasActive, remove, setPinned };
}

/**
 * One job, followed live over SSE.
 *
 * The stream is the primary signal; a slow poll runs alongside it so the UI still
 * converges if the stream drops (a proxy timeout, a laptop sleeping).
 */
export function useJobProgress(jobId, { onFinished } = {}) {
  const toast = useToast();
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(Boolean(jobId));
  const finishedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!jobId) return null;
    try {
      const payload = await batchApi.get(jobId);
      setJob(payload);
      return payload;
    } catch (cause) {
      toast.error(cause, { title: 'Could not read job status' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [jobId, toast]);

  useEffect(() => {
    finishedRef.current = false;
    setSummary(null);
    refresh();
  }, [jobId, refresh]);

  useEffect(() => {
    if (!jobId) return undefined;
    const unsubscribe = batchApi.subscribe(jobId, {
      onEvent: (name, data) => {
        if (name === 'progress') setJob((current) => ({ ...(current || {}), ...data }));
        if (name === 'done') {
          setSummary(data);
          setJob((current) => ({ ...(current || {}), ...data }));
          if (!finishedRef.current) {
            finishedRef.current = true;
            onFinished?.(data);
          }
        }
      },
      onError: () => refresh(),
    });
    return unsubscribe;
  }, [jobId, onFinished, refresh]);

  useEffect(() => {
    if (!jobId || finishedRef.current) return undefined;
    const timer = setInterval(refresh, 8000);
    return () => clearInterval(timer);
  }, [jobId, refresh]);

  const cancel = useCallback(async () => {
    if (!jobId) return;
    try {
      const payload = await batchApi.cancel(jobId);
      setJob((current) => ({ ...(current || {}), ...payload }));
      toast.info('Cancelling', 'The job stops after the image currently in flight.');
    } catch (cause) {
      toast.error(cause);
    }
  }, [jobId, toast]);

  return { job, summary, loading, refresh, cancel };
}
